import React from "react";
import { Box, Container, Typography, Paper, Divider } from "@mui/material";
import { LocationOn, Email, Phone } from "@mui/icons-material";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import PageTitle from "../components/PageTitle";
import termsBanner from "../assets/images/privacy.avif";

const sections = [
  {
    title: "1. Introduction and Acceptance of Terms",
    paragraphs: [
      "Welcome to RestaurantWala. These Terms of Service govern your access to and use of the RestaurantWala website, software, dashboard, digital menu, QR ordering system, billing tools, and related services.",
      "By registering for an account, accessing the platform, or using any RestaurantWala service, you acknowledge that you have read, understood, and agreed to these Terms of Service. If you do not agree with these terms, you should not use the services.",
    ],
  },
  {
    title: "2. About RestaurantWala",
    paragraphs: [
      "RestaurantWala is a restaurant management and digital ordering platform designed to help restaurants manage menus, categories, orders, tables, staff, billing, offers, inventory, customer interactions, and related restaurant operations.",
      "RestaurantWala provides technology and software services. Unless expressly stated otherwise, RestaurantWala does not operate the restaurant, prepare food, deliver food, or act as the restaurant's agent for customer orders.",
    ],
  },
  {
    title: "3. Eligibility",
    paragraphs: [
      "You must have the legal capacity to enter into a binding agreement under applicable law to create and use a RestaurantWala account.",
      "If you create an account on behalf of a restaurant, business, or organization, you represent that you are authorized to do so and to accept these Terms on its behalf.",
    ],
  },
  {
    title: "4. Account Registration and Security",
    paragraphs: [
      "Restaurants may be required to provide accurate and complete information during registration. You are responsible for keeping your account information current and accurate.",
      "You are responsible for maintaining the confidentiality of your login credentials and for activities performed through your account. If you suspect unauthorized access, you should notify RestaurantWala as soon as reasonably possible.",
      "RestaurantWala may use reasonable verification and security measures to protect accounts and platform access.",
    ],
  },
  {
    title: "5. Restaurant Responsibilities",
    paragraphs: [
      "Restaurants using RestaurantWala are responsible for the accuracy and legality of the information they publish through the platform.",
      "This includes menu names, descriptions, prices, taxes, food information, availability, offers, restaurant details, contact information, operating hours, table information, and order-related information.",
      "Restaurants are responsible for fulfilling customer orders accepted through the platform and for complying with all laws, licenses, food-safety requirements, taxation requirements, consumer-protection obligations, and other regulations applicable to their business.",
    ],
  },
  {
    title: "6. Customer Use of the Platform",
    paragraphs: [
      "Customers may access a restaurant's digital menu by scanning a restaurant-provided QR code. Depending on the restaurant's configuration, customers may place orders for DINE_IN, TAKEAWAY, or DELIVERY.",
      "Customers are responsible for reviewing order details before submitting an order and for providing accurate information required to process the order.",
      "RestaurantWala does not guarantee the availability, quality, preparation, delivery, or suitability of food or other products offered by restaurants.",
    ],
  },
  {
    title: "7. QR Codes and Digital Menus",
    paragraphs: [
      "RestaurantWala may provide QR-code functionality for restaurant tables and digital menus.",
      "Restaurants are responsible for placing QR codes in appropriate locations and ensuring that their QR codes and menu information remain accurate and accessible.",
      "QR codes must not be altered, misused, copied for deceptive purposes, or used to direct customers to unauthorized content.",
    ],
  },
  {
    title: "8. Orders, Menu Information and Pricing",
    paragraphs: [
      "Restaurants are responsible for menu availability, item descriptions, prices, applicable taxes, discounts, offers, and other information displayed to customers.",
      "Submitting an order through the platform does not make RestaurantWala the seller of the food or products. The restaurant remains responsible for accepting, preparing, fulfilling, modifying, or cancelling orders according to its applicable policies and legal obligations.",
      "RestaurantWala may provide technical tools for order management but does not guarantee that every order will be accepted or fulfilled.",
    ],
  },
  {
    title: "9. Payments",
    paragraphs: [
      "RestaurantWala may support payment-related options configured by the restaurant, including Cash, UPI, or Card, where available.",
      "Payment processing may involve third-party payment providers, banks, UPI applications, card networks, or other external services. Their terms and policies may apply to the relevant transaction.",
      "RestaurantWala is not responsible for failures, delays, reversals, unauthorized transactions, bank outages, payment-provider issues, or other events outside its reasonable control.",
    ],
  },
  {
    title: "10. Billing and Subscription Services",
    paragraphs: [
      "RestaurantWala may offer paid plans, subscription-based services, or other paid features.",
      "Applicable pricing, billing terms, trial periods, renewal terms, taxes, payment requirements, and other commercial conditions will be communicated to the customer at the time of subscription or purchase.",
      "RestaurantWala may change its pricing or paid features from time to time. Any applicable changes will be communicated through appropriate channels or reflected in the relevant service or subscription information.",
      "Failure to complete applicable payments may result in restriction, suspension, or termination of access to paid services, subject to any applicable contractual or legal requirements.",
    ],
  },
  {
    title: "11. WhatsApp and Third-Party Communication Services",
    paragraphs: [
      "RestaurantWala may provide integrations with WhatsApp or other communication platforms to help restaurants receive or manage order-related communications.",
      "Use of third-party communication services is also subject to the terms, policies, availability, and technical limitations of those third parties.",
      "RestaurantWala is not responsible for outages, message delivery failures, account restrictions, policy enforcement, or changes made by third-party communication providers.",
    ],
  },
  {
    title: "12. Staff Management and Permissions",
    paragraphs: [
      "RestaurantWala may allow restaurant administrators to create staff accounts and assign role-based permissions.",
      "Restaurant administrators are responsible for assigning appropriate permissions, maintaining staff access, and removing access when a staff member no longer requires it.",
      "Actions performed using authorized staff accounts may be treated as actions of the restaurant.",
    ],
  },
  {
    title: "13. Intellectual Property",
    paragraphs: [
      "The RestaurantWala name, logo, software, platform design, user interface, source code, graphics, trademarks, documentation, and other original materials provided by RestaurantWala are owned by or licensed to RestaurantWala and are protected by applicable intellectual-property laws.",
      "You may use the platform only for its intended business or customer purposes. You may not copy, modify, distribute, sell, reverse engineer, reproduce, or create derivative works from RestaurantWala's software or proprietary materials except where permitted by law or with written authorization.",
    ],
  },
  {
    title: "14. Restaurant Content and User Content",
    paragraphs: [
      "Restaurants retain responsibility for content they upload or publish through RestaurantWala, including logos, photographs, menu information, descriptions, offers, and other business content.",
      "By submitting content to RestaurantWala, you grant RestaurantWala the permissions reasonably necessary to host, process, display, transmit, and provide that content as part of the services.",
      "You represent that you have the necessary rights and permissions to submit such content and that it does not unlawfully infringe the rights of another person or organization.",
    ],
  },
  {
    title: "15. Prohibited Activities",
    paragraphs: [
      "You must not use RestaurantWala to:",
      "• upload unlawful, fraudulent, misleading, defamatory, abusive, or infringing content;",
      "• attempt to gain unauthorized access to another account, system, server, or database;",
      "• interfere with or disrupt the operation or security of the platform;",
      "• introduce malware, malicious code, or other harmful software;",
      "• misuse QR codes, customer information, staff accounts, or restaurant accounts;",
      "• use automated methods to access or extract platform data without authorization;",
      "• impersonate another person or organization; or",
      "• use the services for any purpose that violates applicable law.",
    ],
  },
  {
    title: "16. Third-Party Services",
    paragraphs: [
      "RestaurantWala may rely on third-party services for hosting, analytics, cloud storage, communications, payment processing, authentication, mapping, messaging, or other functionality.",
      "Third-party services may have their own terms, privacy policies, limitations, and availability requirements. RestaurantWala does not control all third-party services and cannot guarantee their continuous availability or performance.",
    ],
  },
  {
    title: "17. Service Availability and Changes",
    paragraphs: [
      "RestaurantWala aims to provide reliable services but does not guarantee uninterrupted, error-free, or continuously available operation.",
      "Services may occasionally be unavailable because of maintenance, upgrades, technical issues, network failures, security incidents, third-party outages, or circumstances beyond reasonable control.",
      "RestaurantWala may modify, improve, suspend, or discontinue features or services when reasonably necessary.",
    ],
  },
  {
    title: "18. Account Suspension and Termination",
    paragraphs: [
      "RestaurantWala may suspend or terminate an account where reasonably necessary, including in cases of violation of these Terms, suspected fraud or misuse, security concerns, unlawful activity, non-payment of applicable charges, or actions that may harm the platform or other users.",
      "You may stop using the services and request account closure subject to applicable terms and outstanding obligations.",
      "Termination may result in loss of access to account features and data. Where legally required or commercially appropriate, RestaurantWala may provide reasonable notice or an opportunity to address the relevant issue.",
    ],
  },
  {
    title: "19. Limitation of Liability",
    paragraphs: [
      "To the maximum extent permitted by applicable law, RestaurantWala will not be liable for indirect, incidental, special, consequential, or punitive losses arising from use of or inability to use the services.",
      "RestaurantWala is not responsible for losses caused by restaurant decisions, inaccurate menu information, food quality, order fulfilment, delivery, customer disputes, third-party services, payment failures, internet or network issues, or events beyond its reasonable control.",
      "Nothing in these Terms excludes or limits liability that cannot lawfully be excluded or limited under applicable law.",
    ],
  },
  {
    title: "20. Indemnification",
    paragraphs: [
      "To the extent permitted by applicable law, you agree to defend, indemnify, and hold harmless RestaurantWala and its owners, employees, contractors, and service providers from claims, losses, liabilities, damages, and expenses arising from your unlawful use of the services, violation of these Terms, infringement of third-party rights, or content and information submitted by you.",
    ],
  },
  {
    title: "21. Disclaimer",
    paragraphs: [
      "RestaurantWala provides software and technology services on an as-available basis. Except where expressly stated or required by law, RestaurantWala does not make warranties regarding uninterrupted availability, accuracy of third-party information, or suitability of the platform for every particular business requirement.",
      "Restaurants and customers remain responsible for their own decisions, transactions, content, and compliance with applicable laws.",
    ],
  },
  {
    title: "22. Changes to These Terms",
    paragraphs: [
      "RestaurantWala may update these Terms of Service from time to time to reflect changes in the platform, business practices, legal requirements, or services.",
      "Updated terms may be published on this page with a revised effective or updated date. Continued use of the services after an update may constitute acceptance of the revised Terms, to the extent permitted by applicable law.",
    ],
  },
  {
    title: "23. Governing Law and Jurisdiction",
    paragraphs: [
      "These Terms of Service are governed by the laws of India, subject to applicable law.",
      "Any disputes arising in connection with these Terms or the services will be subject to the jurisdiction of the competent courts having jurisdiction over the applicable location, unless otherwise required by applicable law.",
    ],
  },
  {
    title: "24. Contact Us",
    paragraphs: [
      "If you have questions regarding these Terms of Service, you can contact RestaurantWala using the details below.",
    ],
  },
  {
    title: "25. Support and Grievances",
    paragraphs: [
      "For service-related questions, account issues, privacy concerns, or complaints, users may contact RestaurantWala using the contact information provided below.",
      "RestaurantWala will make reasonable efforts to review and respond to legitimate requests within an appropriate period.",
    ],
  },
];

// const ContactRow = ({ icon, children }) => (
//   <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
//     <Box sx={{ color: "#49d75b", display: "flex", mt: "2px" }}>{icon}</Box>
//     <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
//       {children}
//     </Typography>
//   </Box>
// );

// const Office = ({ title, children }) => (
//   <Box sx={{ mb: 3 }}>
//     <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
//       {title}
//     </Typography>
//     <ContactRow icon={<LocationOn />}>{children}</ContactRow>
//   </Box>
// );

const Office = ({ title, address }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
    <LocationOn
      sx={{
        color: "var(--primary)",
        fontSize: 28,
        mt: 0.2,
        flexShrink: 0,
      }}
    />
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: "var(--text)",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      {address.map((line) => (
        <Typography
          key={line}
          sx={{
            color: "var(--text-light)",
            fontSize: "14px",
            lineHeight: 1.7,
          }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  </Box>
);

const ContactRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
    <Box sx={{ color: "var(--primary)", display: "flex" }}>{icon}</Box>
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: "var(--text)",
          fontSize: "14px",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ color: "var(--text-light)", fontSize: "14px" }}>
        {value}
      </Typography>
    </Box>
  </Box>
);
export default function TermsOfService() {
  return (
    <>
      <PageTitle
        title="Terms of Service – RestaurantWala"
        description="Read the RestaurantWala Terms of Service to understand the rules, responsibilities and conditions for using our restaurant management services."
        keywords="RestaurantWala terms of service, terms and conditions, restaurant software terms, service terms"
      />
      <Navbar />

      {/* <PageBanner title="Terms of Service" /> */}
      <PageBanner
        title="Terms of Service"
        subtitle="Please review the terms and conditions that apply when using RestaurantWala's services and platform."
        chipLabel="TERMS"
        bgImage={termsBanner}
      />

      <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: "#f8faf9" }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: "18px",
              border: "1px solid #e8ece9",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "1.6rem", md: "2rem" },
                fontWeight: 800,
                color: "var(--text)",
                mb: 1,
              }}
            >
              RestaurantWala Terms of Service
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 4 }}>
              These Terms of Service explain the rules and conditions that apply
              when restaurants, customers, staff members, and other users access
              or use RestaurantWala services.
            </Typography>

            {sections.slice(0, 23).map((section) => (
              <Box key={section.title} sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 750,
                    mb: 1.5,
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                  }}
                >
                  {section.title}
                </Typography>

                {section.paragraphs.map((paragraph, index) =>
                  paragraph.startsWith("•") ? (
                    <Typography
                      key={index}
                      variant="body1"
                      sx={{ lineHeight: 1.8, mb: 0.7, pl: 1 }}
                    >
                      {paragraph}
                    </Typography>
                  ) : (
                    <Typography
                      key={index}
                      variant="body1"
                      sx={{ lineHeight: 1.8, mb: 1.4 }}
                    >
                      {paragraph}
                    </Typography>
                  ),
                )}
              </Box>
            ))}

            <Divider sx={{ my: 4 }} />

            {/* <Typography
              variant="h5"
              sx={{ fontWeight: 750, mb: 2, fontSize: { xs: "1.25rem", md: "1.5rem" } }}
            >
              24. Contact Us
            </Typography>

            <Office title="Head Office">
              Shiv Manglam 6, AB 23, Kalwar Rd, Govindpura, Jhotwara,
              Hathod, Jaipur, Rajasthan - 302013
            </Office>

            <Office title="Branch Office">
              Tharhounda Showroom, Gawdi Mod, Neem Ka Thana, Rajasthan -
              332713
            </Office>

            <ContactRow icon={<Email />}>
              restaurantwala24@gmail.com
            </ContactRow>

            <ContactRow icon={<Phone />}>+91 6378788756</ContactRow> */}

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "var(--text)",
                  fontSize: { xs: "1.15rem", md: "1.35rem" },
                  mb: 2,
                }}
              >
                24. Contact Us
              </Typography>

              <Office
                title="Head Office"
                address={[
                  "Shiv Manglam 6, AB 23, Kalwar Rd,",
                  "Govindpura, Jhotwara, Hathod,",
                  "Jaipur, Rajasthan - 302013",
                ]}
              />

              <Office
                title="Branch Office"
                address={[
                  "Tharhounda Showroom, Gawdi Mod,",
                  "Neem Ka Thana, Rajasthan - 332713",
                ]}
              />

              <Box sx={{ mt: 3 }}>
                <ContactRow
                  icon={<Email />}
                  label="Email"
                  value="restaurantwala24@gmail.com"
                />
                <ContactRow
                  icon={<Phone />}
                  label="Phone"
                  value="+91 6378788756"
                />
              </Box>
            </Box>

            <Box sx={{ mt: 5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 750,
                  mb: 1.5,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                25. Support and Grievances
              </Typography>

              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                For service-related questions, account issues, privacy concerns,
                or complaints, please contact RestaurantWala using the email
                address or phone number provided above.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
